import { supabase } from './SupabaseConfig';
import { SettingsRepository, WasteAlertSettings } from '../../domain/settings/ports/SettingsRepository';
import { PosConfig, DEFAULT_POS_CONFIG } from '../../domain/settings/entities/PosConfig';
import { InfrastructureError } from '@core/errors/AppError';

export class SupabaseSettingsRepository implements SettingsRepository {
  async getPosConfig(ownerId: string, locationId: string | null = null): Promise<PosConfig> {
    try {
      let query = supabase
        .from('pos_configs')
        .select('*')
        .eq('owner_id', ownerId);

      if (locationId) {
        query = query.eq('location_id', locationId);
      } else {
        query = query.is('location_id', null);
      }

      const { data, error } = await query.maybeSingle();
      if (error) throw new InfrastructureError(`Failed to fetch POS config: ${error.message}`, 'SupabaseSettingsRepository');
      
      if (!data) return { ...DEFAULT_POS_CONFIG };

      return this.mapToPosConfig(data);
    } catch (err) {
      console.error('[SupabaseSettingsRepository] getPosConfig error:', err);
      return { ...DEFAULT_POS_CONFIG };
    }
  }

  async updatePosConfig(ownerId: string, config: Partial<PosConfig>, locationId: string | null = null): Promise<void> {
    try {
      const payload = {
        owner_id: ownerId,
        location_id: locationId,
        allow_discount: config.allowDiscount,
        allow_inventory_view: config.allowInventoryView,
        allow_order_cancel: config.allowOrderCancel,
        allow_staff_void: config.allowStaffVoid,
        manager_pin: config.managerPin,
        pos_theme: config.posTheme,
        updated_at: new Date().toISOString()
      };

      // Filter out undefined values
      const finalPayload: Record<string, any> = { ...payload };
      Object.keys(finalPayload).forEach(key => finalPayload[key] === undefined && delete finalPayload[key]);

      const { error } = await supabase
        .from('pos_configs')
        .upsert(finalPayload, { onConflict: 'owner_id,location_id' });

      if (error) throw new InfrastructureError(`Failed to update POS config: ${error.message}`, 'SupabaseSettingsRepository');
    } catch (err) {
      console.error('[SupabaseSettingsRepository] updatePosConfig error:', err);
      throw err;
    }
  }

  listenPosConfig(ownerId: string, callback: (config: PosConfig) => void, locationId: string | null = null): () => void {
    const channel = supabase
      .channel(`pos_config_owner_${ownerId}_loc_${locationId || 'global'}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'pos_configs',
        filter: `owner_id=eq.${ownerId}${locationId ? `&location_id=eq.${locationId}` : ''}`
      }, async () => {
        const config = await this.getPosConfig(ownerId, locationId);
        callback(config);
      })
      .subscribe();

    // Initial fetch
    this.getPosConfig(ownerId, locationId).then(callback);

    return () => {
      channel.unsubscribe();
    };
  }

  async getWasteAlertSettings(uid: string): Promise<WasteAlertSettings> {
    try {
      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('owner_id', uid)
        .eq('type', 'waste')
        .maybeSingle();

      if (error) throw error;
      if (!data) return this.getWasteDefaults();

      const s = data.settings || {};
      return {
        wasteThresholdVnd: s.wasteThresholdVnd ?? 500000,
        starMaterialIds: s.starMaterialIds || [],
        webhookUrl: s.webhookUrl || '',
        emailTo: s.emailTo || '',
        brandName: s.brandName || 'Quán',
        dailyReportEnabled: !!s.dailyReportEnabled,
        dailyReportAt: s.dailyReportAt || '23:00',
        weeklyReportEnabled: !!s.weeklyReportEnabled,
        weeklyReportDay: s.weeklyReportDay ?? 1
      };
    } catch (error) {
      console.error('[SupabaseSettingsRepository] getWasteAlertSettings error:', error);
      return this.getWasteDefaults();
    }
  }

  async saveWasteAlertSettings(uid: string, settings: Partial<WasteAlertSettings>): Promise<void> {
    try {
      const { data: existing } = await supabase
        .from('notification_settings')
        .select('settings')
        .eq('owner_id', uid)
        .eq('type', 'waste')
        .maybeSingle();
      
      const newSettings = {
        ...(existing?.settings || {}),
        ...settings
      };

      const { error } = await supabase
        .from('notification_settings')
        .upsert({
          owner_id: uid,
          type: 'waste',
          settings: newSettings,
          updated_at: new Date().toISOString()
        }, { onConflict: 'owner_id,type' });

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi lưu cấu hình thông báo thất thoát', 'SupabaseSettingsRepository', error as Error);
    }
  }

  private getWasteDefaults(): WasteAlertSettings {
    return {
      wasteThresholdVnd: 500000,
      starMaterialIds: [],
      webhookUrl: '',
      emailTo: '',
      brandName: 'Quán',
      dailyReportEnabled: false,
      dailyReportAt: '23:00',
      weeklyReportEnabled: false,
      weeklyReportDay: 1,
    };
  }

  private mapToPosConfig(d: any): PosConfig {
    return {
      allowDiscount: d.allow_discount ?? DEFAULT_POS_CONFIG.allowDiscount,
      allowInventoryView: d.allow_inventory_view ?? DEFAULT_POS_CONFIG.allowInventoryView,
      allowOrderCancel: d.allow_order_cancel ?? DEFAULT_POS_CONFIG.allowOrderCancel,
      allowStaffVoid: d.allow_staff_void ?? DEFAULT_POS_CONFIG.allowStaffVoid,
      managerPin: d.manager_pin ?? DEFAULT_POS_CONFIG.managerPin,
      posTheme: d.pos_theme ?? DEFAULT_POS_CONFIG.posTheme,
      locationId: d.location_id
    };
  }

  // Bill Template Implementation
  async getBillTemplate(uid: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('pos_configs')
        .select('settings_data')
        .eq('owner_id', uid)
        .eq('config_type', 'bill_template')
        .maybeSingle();

      if (error) throw error;
      return data?.settings_data || null;
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy mẫu hóa đơn (Supabase)', 'SupabaseSettingsRepository', error as Error);
    }
  }

  async saveBillTemplate(uid: string, template: any): Promise<void> {
    try {
      const { error } = await supabase
        .from('pos_configs')
        .upsert({
          owner_id: uid,
          config_type: 'bill_template',
          settings_data: template,
          updated_at: new Date().toISOString()
        }, { onConflict: 'owner_id,config_type' });

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi lưu mẫu hóa đơn (Supabase)', 'SupabaseSettingsRepository', error as Error);
    }
  }

  listenBillTemplate(uid: string, callback: (template: any) => void): () => void {
    const channel = supabase
      .channel(`bill_template_${uid}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'pos_configs',
        filter: `owner_id=eq.${uid}`
      }, async () => {
        const template = await this.getBillTemplate(uid);
        if (template) callback(template);
      })
      .subscribe();

    this.getBillTemplate(uid).then(t => {
      if (t) callback(t);
    });

    return () => {
      supabase.removeChannel(channel);
    };
  }

  // Feature Flags Implementation
  async getFeatureFlags(uid: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('settings_data')
        .eq('owner_id', uid)
        .eq('setting_type', 'feature_flags')
        .maybeSingle();

      if (error) throw error;
      return data?.settings_data || { sourcingAiEnabled: false };
    } catch (error) {
      console.error('[SupabaseSettingsRepo] getFeatureFlags failed:', error);
      return { sourcingAiEnabled: false };
    }
  }

  async saveFeatureFlag(uid: string, key: string, value: boolean): Promise<void> {
    try {
      const current = await this.getFeatureFlags(uid);
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          owner_id: uid,
          setting_type: 'feature_flags',
          settings_data: { ...current, [key]: value },
          updated_at: new Date().toISOString()
        }, { onConflict: 'owner_id,setting_type' });

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi lưu cờ tính năng', 'SupabaseSettingsRepository', error as Error);
    }
  }

  listenFeatureFlags(uid: string, callback: (flags: any) => void): () => void {
    const channel = supabase
      .channel(`feature_flags_${uid}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_settings',
        filter: `owner_id=eq.${uid}`
      }, async () => {
        const flags = await this.getFeatureFlags(uid);
        callback(flags);
      })
      .subscribe();

    this.getFeatureFlags(uid).then(callback);

    return () => {
      supabase.removeChannel(channel);
    };
  }

  // AI Preferences Implementation
  async getAiPreferences(uid: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('settings_data')
        .eq('owner_id', uid)
        .eq('setting_type', 'ai_preferences')
        .maybeSingle();

      if (error) throw error;
      return data?.settings_data || {};
    } catch (error) {
      console.error('[SupabaseSettingsRepo] getAiPreferences failed:', error);
      return {};
    }
  }

  async updateAiPreferences(uid: string, delta: any): Promise<void> {
    try {
      const current = await this.getAiPreferences(uid);
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          owner_id: uid,
          setting_type: 'ai_preferences',
          settings_data: { ...current, ...delta },
          updated_at: new Date().toISOString()
        }, { onConflict: 'owner_id,setting_type' });

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi cập nhật AI preferences', 'SupabaseSettingsRepository', error as Error);
    }
  }

  // Roadmap Implementation
  async getRoadmap(uid: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('settings_data')
        .eq('owner_id', uid)
        .eq('setting_type', 'roadmap')
        .maybeSingle();

      if (error) throw error;
      return data?.settings_data || null;
    } catch (error) {
      console.error('[SupabaseSettingsRepo] getRoadmap failed:', error);
      return null;
    }
  }

  async saveRoadmap(uid: string, data: any): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          owner_id: uid,
          setting_type: 'roadmap',
          settings_data: data,
          updated_at: new Date().toISOString()
        }, { onConflict: 'owner_id,setting_type' });

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi lưu lộ trình', 'SupabaseSettingsRepository', error as Error);
    }
  }

  listenRoadmap(uid: string, callback: (data: any) => void): () => void {
    const channel = supabase
      .channel(`roadmap_${uid}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_settings',
        filter: `owner_id=eq.${uid}`
      }, async () => {
        const data = await this.getRoadmap(uid);
        callback(data);
      })
      .subscribe();

    this.getRoadmap(uid).then(callback);

    return () => {
      supabase.removeChannel(channel);
    };
  }
}
